import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import ScreenMedia, { useViewportFormat } from "@/components/ScreenMedia";
import { fileUrl } from "@/lib/upload";
import Leaderboard from "@/components/Leaderboard";
import ChannelRace from "@/components/ChannelRace";
import RulesSourceRace from "@/components/RulesSourceRace";
import { QRCodeSVG } from "qrcode.react";
import { rulesUrl } from "@/components/ContestRules";
import { themeClass } from "@/lib/theme";
import { StoreOfMonthBadge } from "@/components/StoreOfMonthBadge";

const PHASE_FADE = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.5 } };

export default function DisplayScreen() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const storeId = params.get("sede") || null;
  const [contest, setContest] = useState(null);
  const format = useViewportFormat(params.get("format"), contest?.game?.custom_formats);
  const [phase, setPhase] = useState("idle");
  const [play, setPlay] = useState(null);
  const [channels, setChannels] = useState(null);
  const lastSeen = useRef(new Date().toISOString());

  useEffect(() => {
    api.contestPublic(id).then(setContest);
  }, [id]);

  useEffect(() => {
    const t = setInterval(async () => {
      if (phase !== "idle") return;
      try {
        const r = await fetch(`${api.latestPlayUrl(id)}?after=${encodeURIComponent(lastSeen.current)}${storeId ? `&store_id=${storeId}` : ""}`);
        const p = await r.json();
        if (p?.created_at) {
          lastSeen.current = p.created_at;
          setPlay(p);
          setPhase("playing");
          setTimeout(() => setPhase(p.result === "win" ? "win" : "lose"), 3500);
          setTimeout(() => setPhase("idle"), 12000);
        }
      } catch (e) {
        console.debug("DisplayScreen: polling fallito, riprovo", e);
      }
    }, 2000);
    return () => clearInterval(t);
  }, [id, storeId, phase]);

  if (!contest) return <div className="min-h-screen bg-black" />;
  const g = contest.game || {};
  const fit = g.screens_fit || "cover";
  const logo = fileUrl(contest.logo_file_id) || contest.logo_url;

  return (
    <div className={`fixed inset-0 overflow-hidden text-white ${themeClass(g.bg_color, !!(g.screens && g.screens.length))}`} style={{ background: g.bg_color || "#ffffff" }} data-testid="display-screen" data-format={format} data-phase={phase}>
      <AnimatePresence mode="wait">
        <motion.div key={phase} {...PHASE_FADE} className="absolute inset-0">
          <ScreenMedia screens={g.screens} phase={phase} format={format} fit={fit}>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
          </ScreenMedia>
          <div className="absolute inset-0 flex flex-col items-center justify-end p-[6vmin] text-center">
            {phase === "idle" && (
              <>
                <div className="rounded-3xl bg-white/95 p-[2vmin] shadow-2xl mb-[3vmin]">
                  {logo ? <img src={logo} alt="" className="h-[14vmin] w-auto object-contain" /> : <Logo className="h-[14vmin]" />}
                </div>
                <h1 className="font-display font-black text-[8vmin] leading-none drop-shadow-lg">{g.welcome || contest.name}</h1>
                <p className="text-[3vmin] text-white/85 mt-[2vmin] max-w-[80vw]">{g.instructions}</p>
                {g.show_leaderboard !== false && (
                  <div className="mt-[3vmin] w-full flex flex-col items-center">
                    <Leaderboard contestId={id} accent={g.accent_color} onData={(d) => setChannels(d.channels_today)} />
                    {(contest.whatsapp_number || contest.channel === "whatsapp" || channels?.some((c) => c.key === "whatsapp" && c.plays > 0)) && (
                      <ChannelRace channels={channels} accent={g.accent_color} />
                    )}
                    {(contest.rules_text || contest.rules_file_id) && <RulesSourceRace contestId={id} />}
                  </div>
                )}
              </>
            )}
            {phase === "playing" && <h1 className="font-display font-black text-[9vmin] animate-pulse drop-shadow-lg">Buona fortuna…</h1>}
            {phase === "win" && (
              <>
                <h1 className="font-display font-black text-[10vmin] drop-shadow-lg" style={{ color: g.accent_color || "#f59e0b" }}>{g.win_title || "HAI VINTO!"}</h1>
                <p className="text-[5vmin] font-bold mt-[1vmin]">{play?.prize_name}{play?.prize_value ? ` · ${play.prize_value}` : ""}</p>
                <p className="text-[2.5vmin] text-white/80 mt-[1vmin]">{g.win_message}</p>
              </>
            )}
            {phase === "lose" && (
              <>
                <h1 className="font-display font-black text-[9vmin] drop-shadow-lg">{g.lose_title || "Non hai vinto"}</h1>
                <p className="text-[3vmin] text-white/85 mt-[1vmin]">{g.lose_message}</p>
              </>
            )}
          </div>
          {phase === "idle" && <StoreOfMonthBadge storeId={storeId} contestId={id} accent={g.accent_color} />}
          {phase === "idle" && (contest.rules_text || contest.rules_file_id) && (
            <div className="absolute bottom-[3vmin] right-[3vmin] rounded-2xl bg-white/95 text-slate-900 p-[1.5vmin] shadow-2xl flex items-center gap-[1.5vmin]" data-testid="display-rules-qr">
              <div className="rounded-lg bg-white p-[0.5vmin]"><QRCodeSVG value={rulesUrl(contest.id, "tv")} size={0} style={{ width: "12vmin", height: "12vmin" }} /></div>
              <div className="text-left pr-[1vmin]"><p className="font-display font-bold text-[2.4vmin] leading-tight">Leggi il<br />regolamento</p><p className="text-[1.6vmin] text-slate-500">Scansiona con il telefono</p></div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
